import { Text } from '@/components/text';
import { Colors } from '@/constants/theme';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import React from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { Tooltip } from './tooltip';

interface SliderProps {
    label?: string;
    tooltip?: string;
    value: number;
    min?: number;
    max?: number;
    onChange: (value: number) => void;
    disabled?: boolean;
}

export function Slider({ label, tooltip, value, min = 0, max = 100, onChange, disabled }: SliderProps) {
    const width = useSharedValue(0);
    const x = useSharedValue(0);

    const onLayout = (event: LayoutChangeEvent) => {
        width.value = event.nativeEvent.layout.width;
        // Initialize x based on current value
        const percentage = (value - min) / (max - min);
        x.value = percentage * width.value;
    };

    // Update x when value changes from outside
    React.useEffect(() => {
        if (width.value > 0) {
            const percentage = (value - min) / (max - min);
            x.value = withTiming(percentage * width.value);
        }
    }, [value, min, max]);

    const gesture = Gesture.Pan()
        .enabled(!disabled)
        .onUpdate((event) => {
            let newX = event.x;
            if (newX < 0) newX = 0;
            if (newX > width.value) newX = width.value;
            x.value = newX;

            const percentage = newX / width.value;
            const newValue = Math.round(min + percentage * (max - min));
            runOnJS(onChange)(newValue);
        });

    const thumbStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: x.value - 8 }],
    }));

    const trackActiveStyle = useAnimatedStyle(() => ({
        width: x.value,
    }));

    return (
        <GestureHandlerRootView style={[styles.outerContainer, disabled && { opacity: 0.5 }]}>
            {label && (
                <View style={styles.labelRow}>
                    <Text style={{ color: Colors.light.primary }}>{label}</Text>
                    {tooltip && (
                        <Tooltip content={tooltip}>
                            <MaterialIcons name="info" size={18} color={Colors.light.primary} />
                        </Tooltip>
                    )}
                </View>
            )}
            <View style={styles.container}>
                <View style={styles.labels}>
                    <Text style={styles.label}>{min}%</Text>
                    <Text style={styles.label}>{max}%</Text>
                </View>
                <GestureDetector gesture={gesture} >
                    <View style={[styles.trackContainer, disabled && { cursor: 'not-allowed' }]} onLayout={onLayout}>
                        <View style={styles.trackBackground} />
                        <Animated.View style={[styles.trackActive, trackActiveStyle]} />
                        <Animated.View style={[styles.thumb, thumbStyle]} />
                    </View>
                </GestureDetector>
            </View>
        </GestureHandlerRootView>
    );
}

const styles = StyleSheet.create({
    outerContainer: {
        width: '100%',
        gap: 10,
    },
    labelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    container: {
        width: '100%',
        paddingVertical: 0,
    },
    labels: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 0,
    },
    label: {
        lineHeight: 14,
        fontSize: 12,
        color: 'rgba(0,0,0,0.45)',
    },
    trackContainer: {
        height: 30,
        justifyContent: 'center',
        cursor: 'pointer',
    } as any,
    trackBackground: {
        height: 4,
        backgroundColor: '#E2E8F0',
        borderRadius: 2,
        width: '100%',
    },
    trackActive: {
        height: 4,
        backgroundColor: Colors.light.primary,
        borderRadius: 2,
        position: 'absolute',
        left: 0,
    },
    thumb: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: '#fff',
        borderWidth: 2,
        borderColor: Colors.light.primary,
        position: 'absolute',
        left: 0,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
    },
});
